import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { collection, query, where, getDocs, getDoc, doc, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import useBoostSettings, { priceForDays } from "../hooks/useBoostSettings";
import { isFeatured } from "../hooks/useVendors";
import { isPremium } from "../hooks/usePlanSettings";
import { sendBoostAlert } from "../lib/email";
import { useLang } from "../i18n/LanguageContext";

export default function BoostBox({ vendor, user }) {
  const { t } = useLang();
  const { settings } = useBoostSettings();
  const [req, setReq] = useState(null);
  const [reqs, setReqs] = useState([]);
  const [stats, setStats] = useState({ views: 0, clicks: 0 });
  const [spots, setSpots] = useState(null);
  const [days, setDays] = useState(7);
  const [sending, setSending] = useState(false);
  const [freeSending, setFreeSending] = useState(false);

  const load = async () => {
    try {
      const q = query(
        collection(db, "boostRequests"),
        where("ownerUid", "==", user.uid),
        where("vendorId", "==", vendor.id)
      );
      const snap = await getDocs(q);
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setReqs(list);
      const open =
        list
          .filter((r) => ["requested", "approved", "active"].includes(r.status))
          .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))[0] || null;
      setReq(open);
    } catch (e) {
      console.warn("Boost request load:", e.message);
    }
    try {
      const s = await getDoc(doc(db, "stats", vendor.id));
      if (s.exists()) setStats({ views: s.data().views || 0, clicks: s.data().clicks || 0 });
    } catch {}
    try {
      const fq = query(collection(db, "vendors"), where("featured", "==", true), where("isActive", "==", true));
      const fsnap = await getDocs(fq);
      setSpots(fsnap.docs.map((d) => d.data()).filter(isFeatured).length);
    } catch (e) {
      console.warn("Spots load:", e.message);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendor.id, user.uid]);

  const price = priceForDays(settings, days);
  const live = isFeatured(vendor);
  const maxSlots = settings.maxSlots || 6;
  const full = spots != null && spots >= maxSlots;
  const daysLeft = vendor.featuredUntil
    ? Math.max(0, Math.ceil((vendor.featuredUntil.seconds * 1000 - Date.now()) / 86400000))
    : null;

  // Premium free boost days: monthly allowance, used days counted this month.
  const premium = isPremium(vendor);
  const monthKey = (s) => {
    if (!s || !s.seconds) return "";
    const d = new Date(s.seconds * 1000);
    return `${d.getFullYear()}-${d.getMonth()}`;
  };
  const nowKey = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${d.getMonth()}`;
  })();
  const freeGrant = settings.freeBoostDays != null ? settings.freeBoostDays : 3;
  const freeUsed = reqs
    .filter((r) => r.free && r.status !== "declined" && monthKey(r.createdAt) === nowKey)
    .reduce((sum, r) => sum + (Number(r.days) || 0), 0);
  const freeRemaining = Math.max(0, freeGrant - freeUsed);

  const submit = async () => {
    setSending(true);
    try {
      await addDoc(collection(db, "boostRequests"), {
        vendorId: vendor.id,
        vendorName: vendor.businessName,
        ownerUid: user.uid,
        days: Number(days),
        price: Number(price),
        status: "requested",
        createdAt: serverTimestamp(),
      });
      try {
        await sendBoostAlert(vendor, Number(days), Number(price));
      } catch {}
      await load();
    } catch (e) {
      console.error("Boost request failed:", e.message);
    } finally {
      setSending(false);
    }
  };

  const useFree = async () => {
    if (freeRemaining <= 0) return;
    setFreeSending(true);
    try {
      await addDoc(collection(db, "boostRequests"), {
        vendorId: vendor.id,
        vendorName: vendor.businessName,
        ownerUid: user.uid,
        days: Number(freeRemaining),
        price: 0,
        free: true,
        status: "requested",
        createdAt: serverTimestamp(),
      });
      try {
        await sendBoostAlert(vendor, Number(freeRemaining), 0);
      } catch {}
      await load();
    } catch (e) {
      console.error("Free boost failed:", e.message);
    } finally {
      setFreeSending(false);
    }
  };

  return (
    <Box>
      <Header>
        <h3>★ {t("boost.title")}</h3>
        {isPremium(vendor) && (
          <Stat>
            {stats.views} {t("boost.views")} · {stats.clicks} {t("boost.contacts")}
          </Stat>
        )}
      </Header>

      {live ? (
        <Live>
          ★ {t("boost.active")}{daysLeft != null ? ` · ${daysLeft} ${t("admin.daysLeft")}` : ""}
        </Live>
      ) : req && req.status === "requested" ? (
        <Note>{t("boost.awaiting")}</Note>
      ) : req && req.status === "approved" ? (
        <Approved>
          {t("boost.approvedPre")} <b>€{req.price}</b> {t("boost.approvedPost")}
        </Approved>
      ) : (
        <>
          {premium && freeRemaining > 0 && (
            <FreeBox>
              <div>
                <strong>★ {freeRemaining} {t("boost.freeDays")}</strong>
                <p>{t("boost.freeNote")}</p>
              </div>
              <button onClick={useFree} disabled={freeSending}>
                {freeSending ? t("boost.sending") : t("boost.useFree")}
              </button>
            </FreeBox>
          )}

          <p className="sub">{t("boost.subtitle")}</p>
          <Spots $full={full}>
            {spots != null ? `${spots}/${maxSlots} ${t("boost.spotsTaken")}` : "…"}
            {!full && spots != null && ` · ${maxSlots - spots} ${t("boost.left")}`}
          </Spots>

          <Bundles>
            {(settings.bundles || []).map((b) => (
              <Bundle key={b.days} $on={Number(days) === Number(b.days)} onClick={() => setDays(Number(b.days))}>
                <strong>{b.days} {t("boost.days")}</strong>
                <span>€{b.price}</span>
              </Bundle>
            ))}
          </Bundles>

          <Custom>
            <label>{t("boost.customDays")}</label>
            <input type="number" min="1" value={days} onChange={(e) => setDays(Number(e.target.value) || 1)} />
            <Price>= €{price}</Price>
          </Custom>

          {full && <FullNote>{t("boost.fullNote")}</FullNote>}

          <Request onClick={submit} disabled={sending}>
            {sending ? t("boost.sending") : t("boost.request")}
          </Request>
        </>
      )}
    </Box>
  );
}

const Box = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.gold};
  border-radius: 16px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 22px;
  .sub { font-size: 13.5px; color: ${({ theme }) => theme.colors.inkSoft}; }
`;

const Header = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  h3 { font-family: ${({ theme }) => theme.font.display}; font-weight: 500; font-size: 20px; }
`;

const FreeBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
  background: ${({ theme }) => theme.colors.greenDeep};
  color: ${({ theme }) => theme.colors.cream};
  border-radius: 12px;
  padding: 14px 16px;
  strong { font-size: 15px; display: block; }
  p { font-size: 12.5px; opacity: 0.85; margin-top: 3px; max-width: 320px; }
  button {
    background: ${({ theme }) => theme.colors.gold};
    color: ${({ theme }) => theme.colors.greenDeep};
    border: none;
    border-radius: 100px;
    font-size: 14px;
    font-weight: 600;
    padding: 11px 22px;
    cursor: pointer;
    white-space: nowrap;
    &:disabled { opacity: 0.6; cursor: default; }
  }
`;

const Stat = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.inkSoft};
`;

const Live = styled.div`
  background: ${({ theme }) => theme.colors.gold};
  color: ${({ theme }) => theme.colors.greenDeep};
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 14px;
  font-weight: 600;
`;

const Note = styled.div`
  background: rgba(201, 182, 142, 0.22);
  color: #9a7b1f;
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 14px;
  font-weight: 500;
`;

const Approved = styled.div`
  background: rgba(47, 74, 60, 0.1);
  color: ${({ theme }) => theme.colors.green};
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 14px;
`;

const Spots = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ $full, theme }) => ($full ? theme.colors.rose : theme.colors.green)};
`;

const Bundles = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const Bundle = styled.button`
  flex: 1;
  min-width: 90px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  background: ${({ $on, theme }) => ($on ? theme.colors.creamDeep : "transparent")};
  border: 1px solid ${({ $on, theme }) => ($on ? theme.colors.green : theme.colors.hair)};
  border-radius: 12px;
  padding: 12px;
  cursor: pointer;
  strong { font-size: 14px; }
  span { font-size: 13px; color: ${({ theme }) => theme.colors.green}; font-weight: 600; }
`;

const Custom = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  label { font-size: 13.5px; color: ${({ theme }) => theme.colors.inkSoft}; }
  input { width: 90px; font-family: inherit; font-size: 15px; border: 1px solid ${({ theme }) => theme.colors.hair}; border-radius: 10px; padding: 9px 12px; outline: none; }
`;

const Price = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.green};
`;

const FullNote = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.inkSoft};
`;

const Request = styled.button`
  align-self: flex-start;
  background: ${({ theme }) => theme.colors.gold};
  color: ${({ theme }) => theme.colors.greenDeep};
  border: none;
  border-radius: 100px;
  font-size: 15px;
  font-weight: 600;
  padding: 13px 30px;
  cursor: pointer;
  transition: 0.2s;
  &:hover { filter: brightness(0.96); }
  &:disabled { opacity: 0.6; cursor: default; }
`;
